import React, { useEffect } from 'react';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Button } from '../../ui/button';
import { Combobox } from '../../ui/combobox';
import { ComboboxWithInput } from '../../ui/combobox-with-input';

const YES_NO_OPTIONS = [{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }];

const ProofingTab = ({ orderData, setOrderData, printingSubstrates, laminationSubstrates, colours }) => {
  const set = (key, val) => setOrderData({ ...orderData, [key]: val });

  // Keep the number of Colour rows in sync with nos_of_cylinders. Grow with
  // empty slots; shrink only if the trimmed entries are empty (avoid clobbering
  // user-entered colours when cylinders is reduced).
  const nosCyl = Math.max(1, parseInt(orderData.nos_of_cylinders, 10) || 0);
  useEffect(() => {
    if (!nosCyl) return;
    const cur = orderData.colours || [''];
    if (cur.length === nosCyl) return;
    if (cur.length < nosCyl) {
      const grown = [...cur, ...Array(nosCyl - cur.length).fill('')];
      setOrderData({ ...orderData, colours: grown });
    } else {
      const trimmed = cur.slice(0, nosCyl);
      const dropped = cur.slice(nosCyl);
      const allDroppedEmpty = dropped.every(c => !c || !String(c).trim());
      if (allDroppedEmpty) setOrderData({ ...orderData, colours: trimmed });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nosCyl]);

  return (
    <div className="space-y-3 pt-3">
      <div className="grid grid-cols-3 gap-3">
        <div>
          <Label className="text-xs">Printing Substrate</Label>
          <div className="mt-1">
            <Combobox options={printingSubstrates.map(s => ({ value: s.substrate, label: s.substrate }))} value={orderData.printing_substrate} onValueChange={(v) => set('printing_substrate', v)} placeholder="Select..." searchPlaceholder="Search..." className="h-9 text-xs" />
          </div>
        </div>
        <div>
          <Label className="text-xs">Lamination With</Label>
          <div className="mt-1">
            <Combobox options={laminationSubstrates.map(s => ({ value: s.substrate, label: s.substrate }))} value={orderData.lamination_with} onValueChange={(v) => set('lamination_with', v)} placeholder="Select..." searchPlaceholder="Search..." className="h-9 text-xs" />
          </div>
        </div>
        <div>
          <Label className="text-xs">Image to Match With</Label>
          <div className="mt-1">
            <Combobox options={[{ value: 'Printout', label: 'Printout' }, { value: "Customer's Sample", label: "Customer's Sample" }]} value={orderData.image_to_match_with} onValueChange={(v) => set('image_to_match_with', v)} placeholder="Select..." className="h-9 text-xs" />
          </div>
        </div>
        <div>
          <Label className="text-xs">Special Colours to Match With</Label>
          <div className="mt-1">
            <Combobox options={[{ value: 'Printout', label: 'Printout' }, { value: "Customer's Sample", label: "Customer's Sample" }]} value={orderData.special_colours_to_match_with} onValueChange={(v) => set('special_colours_to_match_with', v)} placeholder="Select..." className="h-9 text-xs" />
          </div>
        </div>
        <div>
          <Label className="text-xs">Special Colour Ink Provided</Label>
          <div className="mt-1">
            <Combobox options={YES_NO_OPTIONS} value={orderData.special_colour_ink_provided} onValueChange={(v) => set('special_colour_ink_provided', v)} placeholder="Select..." className="h-9 text-xs" />
          </div>
        </div>
        <div>
          <Label className="text-xs">Proofing to be Laminated</Label>
          <div className="mt-1">
            <Combobox options={YES_NO_OPTIONS} value={orderData.proofing_to_be_laminated} onValueChange={(v) => set('proofing_to_be_laminated', v)} placeholder="Select..." className="h-9 text-xs" />
          </div>
        </div>
      </div>

      <div>
        <h4 className="font-medium text-xs text-slate-800 pt-1">Colours</h4>
        <p className="text-[10px] text-slate-500 mb-1">Select from dropdown or type custom colour name</p>
        <div className="space-y-2">
          {orderData.colours.map((colour, index) => (
            <div key={index} className="flex items-end gap-2">
              <div className="flex-1">
                <Label className="text-xs">Colour {index + 1}</Label>
                <div className="mt-1">
                  <ComboboxWithInput
                    options={colours.map(c => ({ value: c.colour, label: c.colour }))}
                    value={colour}
                    onValueChange={(v) => {
                      const newColours = [...orderData.colours];
                      newColours[index] = v;
                      set('colours', newColours);
                    }}
                    placeholder="Select or type colour..."
                    searchPlaceholder="Search or type..."
                    className="h-9 text-xs"
                  />
                </div>
              </div>
              {orderData.colours.length > 1 && (
                <Button type="button" variant="ghost" size="sm" onClick={() => set('colours', orderData.colours.filter((_, i) => i !== index))} className="text-red-500 hover:text-red-700 hover:bg-red-50 mb-0.5 h-8 text-xs">
                  Remove
                </Button>
              )}
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" onClick={() => set('colours', [...orderData.colours, ''])} className="mt-1 h-8 text-xs">
            + Add More Colour
          </Button>
        </div>
      </div>

      <div>
        <Label className="text-xs">Remark</Label>
        <Input className="mt-1 h-9 text-xs bg-white" value={orderData.proofing_remark || ''} onChange={(e) => set('proofing_remark', e.target.value)} placeholder="Enter remark..." />
      </div>
    </div>
  );
};

export default ProofingTab;
